import { useState, useEffect } from "react";
import { Phone, Plug } from "lucide-react";
import { Seo } from "../components/Seo";
import { PageHero } from "../components/PageHero";
import { IntakePreview, INTAKE_FIELDS } from "../components/IntakePreview";
import { Reveal, Eyebrow } from "../components/motion";
import { BK_FORM_URL, PHONE, PHONE_HREF, STEPS } from "../lib/site";

const BKFrame = () => {
  useEffect(() => {
    const src = `${new URL(BK_FORM_URL).origin}/resources/iframeResizer.min.js`;
    const init = () => { window.iFrameResize?.({ checkOrigin: false }, "#iFrameResizer0"); };
    let s = document.querySelector(`script[src="${src}"]`);
    if (s) { init(); return undefined; }
    s = document.createElement("script");
    s.src = src;
    s.defer = true;
    s.onload = init;
    document.body.appendChild(s);
    return undefined;
  }, []);
  return (
    <>
      <iframe id="iFrameResizer0" src={BK_FORM_URL} title="Commercial cleaning proposal request form" scrolling="yes" height="1000px" width="100%" style={{ border: 0 }} className="block min-h-[1000px] w-full" data-testid="bookingkoala-iframe" />
      <p className="border-t border-slate-200 px-6 py-4 text-sm text-slate-600">
        Form not loading?{" "}
        <a href={BK_FORM_URL.replace(/[?&]embed=true/, "")} target="_blank" rel="noopener noreferrer" data-testid="bookingkoala-open-new-tab" className="font-semibold text-ink underline underline-offset-4">Open the quote form in a new tab</a>
        {" "}or call <a href={PHONE_HREF} className="font-semibold text-ink">{PHONE}</a>.
      </p>
    </>
  );
};

const BKSlot = () => BK_FORM_URL ? (
  <BKFrame />
) : (
  <div className="p-8 md:p-12" data-testid="bookingkoala-placeholder">
    <div className="rounded-2xl border-2 border-dashed border-slate-300 p-8 md:p-10">
      <Plug className="h-8 w-8 text-emerald-600" />
      <h3 className="mt-6 font-display text-2xl font-semibold text-ink">BookingKoala quote form slot</h3>
      <p className="mt-3 max-w-lg text-base text-slate-600">Your official BookingKoala Lead (quote) form will render here. It is not connected yet.</p>
      <ol className="mt-6 list-decimal space-y-2 pl-5 text-sm text-slate-600">
        <li>In BookingKoala, open <strong>Leads → Forms</strong> and build the commercial quote form.</li>
        <li>Choose <strong>Options → Embed Code</strong> and copy the form URL from the iframe <code>src</code>.</li>
        <li>Set it as <code>REACT_APP_BOOKINGKOALA_FORM_URL</code> — the form appears here automatically.</li>
      </ol>
      <p className="mt-8 font-mono text-xs uppercase tracking-[0.2em] text-slate-500">Fields to recreate in the BookingKoala form</p>
      <ul className="mt-3 flex flex-wrap gap-2" data-testid="bookingkoala-field-list">
        {Object.values(INTAKE_FIELDS).map((f) => <li key={f} className="rounded-full bg-porcelain px-3 py-1.5 text-xs text-slate-700">{f}</li>)}
      </ul>
    </div>
  </div>
);

const TABS = [["bk", "BookingKoala form"], ["preview", "Intake preview"]];

export default function RequestQuote() {
  const [tab, setTab] = useState(BK_FORM_URL ? "bk" : "preview");
  return (
    <>
      <Seo title="Request a Free Commercial Cleaning Quote | Shine Solutions" description="Request a free commercial cleaning quote for your office, retail space, medical or dental facility or commercial property in Peoria, Glendale or Surprise, AZ." path="/request-quote" />
      <PageHero kicker="Request a free cleaning quote" lines={["Tell us about", "your facility."]} intro="Two minutes of detail now saves a week of back-and-forth. We review every request, then follow up to schedule a walkthrough or consultation and prepare your customized proposal. There is no automatic pricing — every recurring contract is quoted to your facility." />
      <section className="bg-porcelain py-20 md:py-28">
        <div className="mx-auto grid max-w-[1400px] gap-12 px-5 md:px-10 lg:grid-cols-12">
          <div className="lg:col-span-8">
            {!BK_FORM_URL && (
              <div className="mb-4 inline-flex rounded-full border border-slate-300 bg-white p-1" role="tablist">
                {TABS.map(([k, l]) => (
                  <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`quote-tab-${k}`}
                    className={`rounded-full px-5 py-2.5 text-sm transition-colors ${tab === k ? "bg-ink text-white" : "text-slate-600 hover:text-ink"}`}>{l}</button>
                ))}
              </div>
            )}
            <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_30px_80px_-40px_rgba(7,17,31,0.35)]">
              {BK_FORM_URL || tab === "bk" ? <BKSlot /> : <IntakePreview />}
            </div>
          </div>
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-28">
              <Reveal><Eyebrow>What happens next</Eyebrow></Reveal>
              <ol className="mt-6 space-y-5">
                {STEPS.map((s, i) => (
                  <Reveal as="li" key={s.t} delay={0.04 * i} className="flex gap-4">
                    <span className="font-mono text-xs text-emerald-700">{String(i + 1).padStart(2, "0")}</span>
                    <div><p className="font-display font-semibold text-ink">{s.t}</p><p className="mt-1 text-sm text-slate-600">{s.d}</p></div>
                  </Reveal>
                ))}
              </ol>
              <a href={PHONE_HREF} data-testid="quote-call-link" className="mt-10 flex items-center gap-3 rounded-2xl bg-ink p-6 text-white transition-colors hover:bg-[#0E2038]">
                <Phone className="h-5 w-5 text-glint" /><span><span className="block text-xs text-slate-400">Prefer to talk?</span><span className="font-display text-xl">{PHONE}</span></span>
              </a>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
